/**
 * Stage Category Constants (shared)
 *
 * Canonical source of truth for the CaseStageCategory enum, per-category
 * metadata (label, description, default sort order), and KPI groupings.
 *
 * Backend re-exports from here. Frontend imports via @shared/.
 *
 * The string values MUST stay in lock-step with the Prisma
 * `CaseStageCategory` enum (schema.prisma) — the backend continues to import
 * the Prisma enum in query context, so divergence would break Record<> typing
 * downstream.
 */

/* eslint-disable perfectionist/sort-enums -- ordered by case lifecycle, not alphabetically */
export enum CaseStageCategory {
  UNCATEGORIZED = "UNCATEGORIZED",
  LEAD_INTAKE = "LEAD_INTAKE",
  CASE_BUILDING = "CASE_BUILDING",
  PRE_LIT = "PRE_LIT",
  LITIGATION = "LITIGATION",
  RESOLUTION_PAYOUT = "RESOLUTION_PAYOUT",
  SETTLED = "SETTLED",
  ON_HOLD = "ON_HOLD",
  FALLOUT = "FALLOUT",
  EXCLUDED = "EXCLUDED",
}
/* eslint-enable perfectionist/sort-enums */

export interface StageCategoryMeta {
  description: string;
  label: string;
  sortOrder: number;
}

/**
 * Metadata for each stage category — label, description, and default sort order.
 */
export const STAGE_CATEGORY_META: Record<CaseStageCategory, StageCategoryMeta> = {
  [CaseStageCategory.CASE_BUILDING]: {
    description: "Retained, building case",
    label: "Case Building",
    sortOrder: 2,
  },
  [CaseStageCategory.EXCLUDED]: {
    description: "Duplicate, referred, etc.",
    label: "Excluded",
    sortOrder: 9,
  },
  [CaseStageCategory.FALLOUT]: {
    description: "Rejected, stayed, non-responsive",
    label: "Fallout",
    sortOrder: 8,
  },
  [CaseStageCategory.LEAD_INTAKE]: {
    description: "Unretained and intake",
    label: "Lead & Intake",
    sortOrder: 1,
  },
  [CaseStageCategory.LITIGATION]: {
    description: "Discovery, trial prep, appeals",
    label: "Litigation",
    sortOrder: 4,
  },
  [CaseStageCategory.ON_HOLD]: {
    description: "On Hold, Archived, Inventoried",
    label: "On Hold",
    sortOrder: 7,
  },
  [CaseStageCategory.PRE_LIT]: {
    description: "Litigation track opened",
    label: "Pre-Lit",
    sortOrder: 3,
  },
  [CaseStageCategory.RESOLUTION_PAYOUT]: {
    description: "Settlement, payout",
    label: "Resolution & Payout",
    sortOrder: 5,
  },
  [CaseStageCategory.SETTLED]: {
    description: "Settled and closed",
    label: "Settled",
    sortOrder: 6,
  },
  [CaseStageCategory.UNCATEGORIZED]: {
    description: "All stages must be categorized.",
    label: "Uncategorized",
    sortOrder: 0,
  },
};

/**
 * Synthetic rawStage value used to represent cases whose CRM-provided
 * caseStage is null or blank. Admins can map this sentinel into any
 * category; the sync pipeline and admin recategorization SQL both
 * translate it back to `caseStage IS NULL OR TRIM(caseStage) = ''`
 * when matching against real cases.
 *
 * The double-underscored identifier makes this unambiguously a sentinel —
 * no CRM emits this value, so there is no risk of collision with real data.
 * The user-facing label lives in `MISSING_STATUS_LABEL` only; if we ever
 * want to change the copy we change one constant without a data migration.
 */
export const MISSING_STATUS_RAW_STAGE = "__MISSING_STATUS__";
export const MISSING_STATUS_LABEL = "Missing Status";

/**
 * Set of valid CaseStageCategory enum keys. Used by toStageCategoryLabel
 * for safe recognition that does NOT walk the prototype chain — bare
 * `key in STAGE_CATEGORY_META` would let inputs like `"toString"` or
 * `"__proto__"` through, then look up an undefined entry. Object.hasOwn
 * is the canonical solution but requires ES2022 lib + tsconfig — the
 * shared workspace is consumed by both backend and frontend so we use
 * a plain Set for portability.
 */
const KNOWN_STAGE_CATEGORY_KEYS: ReadonlySet<string> = new Set(Object.keys(STAGE_CATEGORY_META));

/**
 * Convert a `CaseStageCategory` enum value (from the DB / Prisma) into its
 * human-readable label (e.g. `LEAD_INTAKE` → `"Lead & Intake"`). Used by
 * the AI chat snapshot pipeline so the AI surfaces the same labels users
 * see in the dashboards rather than raw enum constants like LEAD_INTAKE.
 *
 * Returns `null` for nullish, empty-string, or non-string inputs — callers
 * should treat null as "no stage info, skip the field". This intentionally
 * does NOT stringify falsy primitives: emitting a literal `"null"` or
 * `"undefined"` bucket into chat aggregations would surface as a real
 * stage category to the AI, which is worse than dropping the row silently.
 *
 * For unknown string inputs (stale snapshots, schema drift, unexpected
 * CRM data) returns the input unchanged so it still shows up as a real
 * bucket the AI can reason about. Idempotent on already-converted labels
 * via the same passthrough — recognition uses an explicit key Set so
 * prototype-chain keys like `"toString"` or `"__proto__"` go through the
 * passthrough branch and return their own string.
 */
export function toStageCategoryLabel(value: unknown): null | string {
  if (typeof value !== "string" || value.length === 0) return null;
  if (KNOWN_STAGE_CATEGORY_KEYS.has(value)) {
    return STAGE_CATEGORY_META[value as CaseStageCategory].label;
  }
  return value;
}

/**
 * Categories in canonical display order for the admin Kanban board.
 * UNCATEGORIZED first so admins see unmapped stages immediately.
 */
export const STAGE_CATEGORIES_IN_ORDER: CaseStageCategory[] = [
  CaseStageCategory.UNCATEGORIZED,
  CaseStageCategory.LEAD_INTAKE,
  CaseStageCategory.CASE_BUILDING,
  CaseStageCategory.PRE_LIT,
  CaseStageCategory.LITIGATION,
  CaseStageCategory.RESOLUTION_PAYOUT,
  CaseStageCategory.SETTLED,
  CaseStageCategory.ON_HOLD,
  CaseStageCategory.FALLOUT,
  CaseStageCategory.EXCLUDED,
];
